import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiMenu, FiX, FiChevronDown } from "react-icons/fi";
import defaultLogo from "../assets/logo.png";
import { fetchMenu, buildMenuTree, fetchNavbarSettings, fetchSiteIdentity } from "../api";

const fallbackTree = [
  { id: 1, title: "Home", slug: "home", children: [] },
  { id: 2, title: "Services", slug: "services", children: [] },
  { id: 3, title: "Virtual Accounting", slug: "virtual-accounting", children: [] },
  { id: 4, title: "Contact", slug: "contact", children: [] },
];

const defaultNavbarSettings = {
  login_text: "Login",
  login_url: "#",
  login_visible: true,
  quote_text: "Get Quote",
  quote_url: "/contact",
  quote_visible: true,
};

const pagePath = (slug) => (slug === "home" ? "/" : `/${slug}`);

// A configurable nav CTA button — internal paths use client-side routing,
// anything else (hash anchors, external URLs) falls back to a plain link.
function NavCTAButton({ href, className, children }) {
  if (href.startsWith("/")) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }
  const isExternal = /^https?:\/\//.test(href);
  return (
    <a href={href} className={className} {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
      {children}
    </a>
  );
}

// Desktop: a sub-menu column inside the mega menu, with its own sub-sub list beneath it.
function MegaMenuColumn({ item }) {
  return (
    <div>
      <Link
        to={pagePath(item.slug)}
        className="block font-semibold text-white hover:text-accent-light transition-colors duration-200 mb-2"
      >
        {item.title}
      </Link>
      {item.children.length > 0 && (
        <ul className="space-y-1.5">
          {item.children.map((child) => (
            <li key={child.id}>
              <Link
                to={pagePath(child.slug)}
                className="text-sm text-gray-400 hover:text-white transition-colors duration-200"
              >
                {child.title}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Desktop: top-level main menu item. If it has sub items, hovering opens a full-width mega menu.
// The panel is `absolute`, anchored to the fixed <header> (the nearest positioned ancestor), with
// plain `left-0 w-full` — that's what makes it flush against the true left edge at the true full
// viewport width, instead of the earlier left-1/2/-translate-x-1/2/w-screen trick, which only lands
// correctly on X=0 when the trigger itself happens to be centered in the viewport (it isn't).
// `.group` uses `self-stretch` so its own hoverable box fills the header's full height — otherwise,
// since the panel now starts at the header's true bottom edge rather than the (vertically centered,
// shorter) button's own bottom edge, the extra header height below the button would be a dead zone
// that drops the CSS :hover chain on the way down to the panel.
function NavItem({ item }) {
  const hasChildren = item.children.length > 0;
  if (!hasChildren) {
    return (
      <Link to={pagePath(item.slug)} className="text-gray-300 hover:text-white transition-colors duration-200">
        {item.title}
      </Link>
    );
  }
  // A menu whose sub items have no sub-sub items of their own (e.g. Blogs) is a simple dropdown list,
  // one item below the other; menus with deeper levels (Services, AI Tools) open the full-width mega menu.
  const isSimpleList = item.children.every((child) => child.children.length === 0);
  if (isSimpleList) {
    return (
      <div className="group relative self-stretch flex items-center">
        <button className="flex items-center gap-1 text-gray-300 hover:text-white transition-colors duration-200">
          {item.title} <FiChevronDown size={14} />
        </button>
        <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 absolute top-full left-1/2 -translate-x-1/2 min-w-[220px] z-40">
          <ul className="bg-navy-darker border border-navy-border rounded-b-xl shadow-2xl py-2">
            {item.children.map((child) => (
              <li key={child.id}>
                <Link
                  to={pagePath(child.slug)}
                  className="block px-5 py-2.5 text-sm text-gray-300 whitespace-nowrap hover:bg-white/5 hover:text-accent-light transition-colors duration-200"
                >
                  {child.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }
  return (
    <div className="group self-stretch flex items-center">
      <button className="flex items-center gap-1 text-gray-300 hover:text-white transition-colors duration-200">
        {item.title} <FiChevronDown size={14} />
      </button>
      <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 absolute top-full left-0 w-full z-40">
        <div className="w-full bg-navy-darker border-y border-navy-border shadow-2xl">
          <div className="w-full px-10 xl:px-20 py-8 grid grid-cols-3 lg:grid-cols-6 gap-8">
            {item.children.map((child) => (
              <MegaMenuColumn key={child.id} item={child} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Mobile: nested accordion using native <details>, one level deeper each recursion.
function MobileNavItem({ item, onNavigate }) {
  const hasChildren = item.children.length > 0;
  if (!hasChildren) {
    return (
      <Link to={pagePath(item.slug)} onClick={onNavigate} className="block py-2 hover:text-accent-light transition-colors duration-200">
        {item.title}
      </Link>
    );
  }
  return (
    <details className="group">
      <summary className="flex items-center justify-center gap-2 py-2 cursor-pointer list-none hover:text-accent-light transition-colors duration-200">
        {item.title} <FiChevronDown size={14} className="group-open:rotate-180 transition-transform" />
      </summary>
      <div className="pb-2 space-y-1 text-gray-400">
        {item.children.map((child) => (
          <MobileNavItem key={child.id} item={child} onNavigate={onNavigate} />
        ))}
      </div>
    </details>
  );
}

// `logo` overrides the site logo (the Co-Work pages have their own).
export default function Header({ logo }) {
  const [open, setOpen] = useState(false);
  const [menuTree, setMenuTree] = useState(fallbackTree);
  const [navbarSettings, setNavbarSettings] = useState(defaultNavbarSettings);
  const [logoUrl, setLogoUrl] = useState(defaultLogo);
  const [siteTitle, setSiteTitle] = useState("Mr. Aali Online Technologies");

  useEffect(() => {
    fetchMenu()
      .then((items) => setMenuTree(buildMenuTree(items)))
      .catch(() => {});
    fetchNavbarSettings().then(setNavbarSettings).catch(() => {});
    fetchSiteIdentity()
      .then((identity) => {
        if (identity.logo) setLogoUrl(identity.logo);
        setSiteTitle(identity.site_title);
      })
      .catch(() => {});
  }, []);

  return (
    <header className="fixed top-0 left-0 w-full z-50 bg-navy-darker/95 backdrop-blur-md shadow-lg transition-all duration-300">
      <div className="h-16 flex items-stretch justify-between px-6 md:px-10">
        <Link to="/" className="flex items-center">
          <motion.img
            src={logo || logoUrl}
            alt={siteTitle}
            className="h-10 w-auto"
            whileHover={{ scale: 0.96 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8 font-medium">
          {menuTree.map((item) => (
            <NavItem key={item.id} item={item} />
          ))}
          <div className="flex items-center gap-3">
            {navbarSettings.login_visible && (
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <NavCTAButton
                  href={navbarSettings.login_url}
                  className="block px-5 py-2 rounded-full text-gray-200 font-medium border border-navy-border hover:border-accent-light hover:text-white transition-colors duration-200"
                >
                  {navbarSettings.login_text}
                </NavCTAButton>
              </motion.div>
            )}
            {navbarSettings.quote_visible && (
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <NavCTAButton
                  href={navbarSettings.quote_url}
                  className="block px-6 py-2 rounded-full text-white font-medium bg-brand-gradient shadow-md shadow-accent/20 hover:shadow-accent-light/30 transition-shadow duration-200"
                >
                  {navbarSettings.quote_text}
                </NavCTAButton>
              </motion.div>
            )}
          </div>
        </nav>

        <div className="lg:hidden flex items-center text-gray-200">
          <button onClick={() => setOpen(!open)} aria-label="Toggle menu">
            {open ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="lg:hidden bg-navy-darker border-t border-navy-border text-gray-200"
        >
          <div className="flex flex-col gap-1 p-6 text-center">
            {menuTree.map((item) => (
              <MobileNavItem key={item.id} item={item} onNavigate={() => setOpen(false)} />
            ))}
            {navbarSettings.login_visible && (
              <NavCTAButton
                href={navbarSettings.login_url}
                className="block w-full mt-4 py-2 rounded-full border border-navy-border text-center"
              >
                {navbarSettings.login_text}
              </NavCTAButton>
            )}
            {navbarSettings.quote_visible && (
              <NavCTAButton
                href={navbarSettings.quote_url}
                className="block w-full mt-2 py-2 rounded-full bg-brand-gradient text-white font-medium text-center"
              >
                {navbarSettings.quote_text}
              </NavCTAButton>
            )}
          </div>
        </motion.div>
      )}
    </header>
  );
}
